/**
 * Environment resolution for the API and auth hosts.
 *
 * Matches intellimorph-tools-newui-react: env values are host suffixes
 * (e.g. invoicemanager.im), and the tenant subdomain is prepended at runtime.
 * On localhost the tenant is always devintellimorph.
 */

export const isLocalhost = () =>
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

/** Tenant slug: first subdomain when deployed, devintellimorph on localhost. */
export function getTenant() {
  if (isLocalhost()) return "devintellimorph";
  const host = window.location.hostname;
  const segments = host.split(".").filter(Boolean);
  return segments.length > 2 ? segments[0] : "";
}

function toUrl(raw) {
  const value = String(raw ?? "").trim();
  if (!value) {
    throw new Error(
      "VITE_API_URL is not set. Copy .env.example to .env.dev (or .env.local) and fill in the API host.",
    );
  }
  return new URL(value.startsWith("http") ? value : `https://${value}`);
}

/** Strip an existing tenant prefix so CI can pass either invoicemanager.im or a full host. */
function baseDomainFromEnv(raw) {
  const url = toUrl(raw);
  const parts = url.hostname.split(".").filter(Boolean);
  if (parts.length > 2) {
    url.hostname = parts.slice(-2).join(".");
  }
  return url;
}

function prefixTenant(url) {
  const tenant = getTenant();
  if (tenant) {
    url.hostname = `${tenant}.${url.hostname}`;
  }
  return url;
}

/** Normalise "1" or "v1" → "v1". */
function versionSegment(raw, fallback = "1") {
  const value = String(raw ?? fallback).trim().replace(/^v/i, "");
  return value ? `v${value}` : "v1";
}

export function getApiVersion() {
  return versionSegment(import.meta.env.VITE_API_VERSION);
}

export function getAuthApiVersion() {
  return versionSegment(import.meta.env.VITE_AUTH_API_VERSION);
}

export function getBaseUrl() {
  return `${prefixTenant(baseDomainFromEnv(import.meta.env.VITE_API_URL)).origin}/${getApiVersion()}/`;
}

/** Auth portal origin for profile links and avatars. Falls back to the API host. */
export function getAuthURL() {
  const raw = import.meta.env.VITE_AUTH_URL || import.meta.env.VITE_API_URL;
  return prefixTenant(baseDomainFromEnv(raw)).origin;
}

/** Auth portal API base, e.g. https://devintellimorph.intellimorph.id/v1 */
export function getAuthApiBaseUrl() {
  return `${getAuthURL()}/${getAuthApiVersion()}`;
}

/**
 * Localhost-only escape hatch so the app can be run without the SSO cookie flow.
 * Deliberately returns null anywhere else, so a leaked value cannot be used in
 * a deployed environment.
 */
export function getDevJwtToken() {
  if (!isLocalhost()) return null;
  const token = import.meta.env.VITE_JWT_TOKEN;
  return typeof token === "string" && token.trim() ? token.trim() : null;
}

/** Localhost never runs the hosted SSO redirect — only the bearer token from env. */
export function usesLocalJwtAuth() {
  return Boolean(getDevJwtToken());
}

/**
 * Axios config for the requests that sit outside RTK Query (login, logout, file
 * download). Cookie auth in deployed environments, bearer token on localhost.
 */
export function getAuthRequestConfig(extraHeaders = {}) {
  const devToken = getDevJwtToken();

  if (!devToken) {
    return { withCredentials: true, headers: { ...extraHeaders } };
  }

  return {
    withCredentials: false,
    headers: { ...extraHeaders, Authorization: `Bearer ${devToken}` },
  };
}
